'use client'

import { NewsArticle } from '@/models/news.model'
import { fetchClassifieds } from '@/services/adsService'
import { useInfiniteQuery } from '@tanstack/react-query'
export interface UseClassifiedsParams {
  page?: number
  size?: number
  slug?: string
  categorySlug?: string
}

const DEFAULT_PAGE_SIZE = 10

export const useClassifieds = ({
  page = 1,
  size = DEFAULT_PAGE_SIZE,
  slug = '',
  categorySlug = '',
}: UseClassifiedsParams = {}) => {
  const queryKey = ['classifieds', { slug, categorySlug, size, page }]

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery<
    {
      items: NewsArticle[]
      page: number
      totalPage: number
    },
    Error
  >({
    queryKey,
    queryFn: () => fetchClassifieds(page, size, slug, categorySlug),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.page
      const totalPages = lastPage.totalPage
      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    getPreviousPageParam: (firstPage) => {
      const currentPage = firstPage.page
      return currentPage > 1 ? currentPage - 1 : undefined
    },
    initialPageParam: 1,
    refetchOnWindowFocus: false,
  })

  const allClassifieds = data?.pages.flatMap((page) => page.items) || []
  const totalPages = data?.pages[data.pages.length - 1]?.totalPage || 1

  return {
    allClassifieds,
    totalPages,
    isLoading,
    isError,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  }
}
